import { useMemo, useState } from 'react';
import type { Dayjs } from 'dayjs';
import { DatePicker } from 'antd';
import { PageHeader } from '../components/yeni-design/primitives';
import { PeriodTabs, type DashboardPeriod } from '../components/PeriodTabs';
import { donemHesapla, donemAraligiMetni, type Donem } from '../components/satis-analiz/donem';
import { OzetSerit } from '../components/satis-analiz/OzetSerit';
import { KanalBlok } from '../components/satis-analiz/KanalBlok';
import { SubeBlok } from '../components/satis-analiz/SubeBlok';
import { ZamanBlok } from '../components/satis-analiz/ZamanBlok';
import { KategoriBlok } from '../components/satis-analiz/KategoriBlok';
import { DikkatBlok } from '../components/satis-analiz/DikkatBlok';
import type { PanelScreen, ReportConfig } from '../types/panelConfig';

const { RangePicker } = DatePicker;

const VARSAYILAN_BLOKLAR = ['ozet', 'kanal', 'sube', 'zaman', 'kategori', 'dikkat'];

interface SatisAnalizProps {
  screen: PanelScreen;
  reports: ReportConfig[];
}

export function SatisAnaliz({ screen, reports }: SatisAnalizProps) {
  const [period, setPeriod] = useState<DashboardPeriod>('aylik');
  const [ozelAralik, setOzelAralik] = useState<[Dayjs, Dayjs] | null>(null);

  const donem: Donem = useMemo(() => {
    if (period === 'ozel' && ozelAralik) {
      const bas = ozelAralik[0].format('YYYY-MM-DD');
      const bit = ozelAralik[1].format('YYYY-MM-DD');
      return { bas, bit, tekGun: bas === bit };
    }
    return donemHesapla(period);
  }, [period, ozelAralik]);

  const baslik = screen.config?.baslik?.trim() || screen.title;
  const bloklar = screen.config?.bloklar ?? VARSAYILAN_BLOKLAR;

  // Task 5-9 bloklari `ortak`'i kullanacak.
  const ortak = { reports, donem, etiketler: screen.config?.etiketler ?? {} };

  return (
    <div className="flex flex-col gap-4">
      <PageHeader
        title={baslik}
        actions={
          <div className="flex items-center gap-2" data-testid="donem-secici">
            <PeriodTabs value={period} onChange={setPeriod} />
            {/* Secili donemin gercek araligi — yalniz "Ozel" degil HER
                sekmede gorunur; kullanici "Aylik" derken hangi tarihlere
                baktigini burada okur. */}
            <span className="text-[12px] text-ink-500 tnum" data-testid="donem-araligi">
              {donemAraligiMetni(donem)}
            </span>
            {period === 'ozel' && (
              // RangePicker `data-testid`'yi HEM baslangic HEM bitis input'una
              // yayar (iki eslesme -> getByTestId hata verir); testid'yi
              // sarmalayici tek div'e koy.
              <div data-testid="ozel-aralik">
                <RangePicker
                  value={ozelAralik}
                  onChange={(v) =>
                    setOzelAralik(v && v[0] && v[1] ? [v[0], v[1]] : null)
                  }
                  format="DD.MM.YYYY"
                  allowClear
                />
              </div>
            )}
          </div>
        }
      />
      {bloklar.map((blok) => {
        switch (blok) {
          // Task 5-9 bloklari buraya baglanir. Bilinmeyen anahtar SESSIZCE atlanir
          // ki ileride blok eklendiginde eski config'ler kirilmasin.
          case 'ozet':
            return <OzetSerit key={blok} reports={ortak.reports} donem={ortak.donem} />;
          case 'kanal':
            return <KanalBlok key={blok} reports={ortak.reports} donem={ortak.donem} />;
          case 'sube':
            return <SubeBlok key={blok} reports={ortak.reports} donem={ortak.donem} />;
          case 'zaman':
            return <ZamanBlok key={blok} reports={ortak.reports} donem={ortak.donem} />;
          case 'kategori':
            return <KategoriBlok key={blok} reports={ortak.reports} donem={ortak.donem} />;
          case 'dikkat':
            return <DikkatBlok key={blok} reports={ortak.reports} donem={ortak.donem} />;
          default:
            return null;
        }
      })}
    </div>
  );
}
